/**
 * The `Rules` card: this pool's whole rules document, read live from
 * `POST /pool/rules-get` on every mount. The Commissioner's own preamble
 * renders verbatim per decision C18 above the auto-generated summary, and
 * the card clamps with an in-place expand. Never a cached copy: SUR-15 and
 * SUR-52 let a Commissioner change the rules at any time, so
 * `27-pool-settings.md` section 27.1 item 3 requires this to show what the
 * rules are now.
 *
 * This is the member-readable mount. The Commissioner and Admin mount is
 * `features/poolSettings/PoolSettingsRulesCard.tsx`, and both draw the same
 * `features/poolRules/PoolRulesBody.tsx`.
 */
import {
  useContestList,
  useContestRulesGet,
} from "@sportsmet/contest-sdk/private";
import { usePoolRulesGet } from "@sportsmet/pool-sdk/private";
import { Button, Card } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { PoolRulesBody } from "../poolRules/PoolRulesBody";
import { resolvePoolRulesState } from "../poolRules/poolRulesState";
import {
  RULES_CARD_TITLE,
  RULES_SHOW_LESS_LABEL,
  RULES_SHOW_MORE_LABEL,
} from "./poolHomeState";

export type typePoolHomeRulesCardProps = { poolId: string };

export const PoolHomeRulesCard = ({
  poolId,
}: typePoolHomeRulesCardProps): ReactNode => {
  const [isExpanded, setIsExpanded] = useState(false);
  const rules = usePoolRulesGet(
    { poolId },
    { staleTime: 0, refetchOnMount: "always" },
  );
  const contests = useContestList({ poolId });
  const viewerContestId = contests.data?.data?.viewerContestId ?? "";
  const contestRules = useContestRulesGet(
    { poolId, contestId: viewerContestId },
    {
      enabled: viewerContestId !== "",
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
  const contestRulesData = contestRules.data?.data ?? null;
  const state = resolvePoolRulesState({
    isLoading: rules.isLoading,
    isTransportError: rules.isError,
    errorCode: extractErrorCode(rules.data),
    commissionerPreamble: rules.data?.data?.commissionerPreamble ?? null,
    autoGeneratedSummary: rules.data?.data?.autoGeneratedSummary ?? null,
    // The contest shown is the one /contest/list names in viewerContestId
    // (ISSUE-1041 owner default 3).
    contest: contestRulesData
      ? {
          autoGeneratedText: contestRulesData.autoGeneratedText,
          commissionerPreamble: contestRulesData.commissionerPreamble ?? null,
        }
      : null,
  });

  return (
    <Card title={RULES_CARD_TITLE} data-testid="pool-home-rules">
      <PoolRulesBody
        state={state}
        doClamp={!isExpanded}
        onRetry={() => {
          void rules.refetch();
        }}
      />
      {state.phase === "ready" ? (
        <Button
          variant="link"
          size="sm"
          data-testid="pool-home-rules-toggle"
          onClick={() => {
            setIsExpanded((current) => !current);
          }}
        >
          {isExpanded ? RULES_SHOW_LESS_LABEL : RULES_SHOW_MORE_LABEL}
        </Button>
      ) : null}
    </Card>
  );
};
